/**
 * Full-screen "Live update in progress" overlay.
 *
 * Shown while the app self-heals from a stale deploy (chunk load failure or a
 * detected new version) just before a reload. Deliberately framework-free
 * vanilla DOM so it can be rendered from global event handlers even when the
 * React tree itself has crashed on a failed chunk import.
 */

const OVERLAY_ID = 'live-update-overlay';

/** Idempotently show the overlay. Safe to call multiple times. */
export function showLiveUpdateOverlay(subtitle?: string): void {
  if (document.getElementById(OVERLAY_ID)) return;

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:2147483647',
    'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
    'gap:20px', 'background:rgba(255,255,255,0.96)', 'backdrop-filter:blur(4px)',
    "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    'text-align:center', 'padding:24px',
  ].join(';');

  // Spinner (brand green) — keyframes injected once alongside the overlay.
  const style = document.createElement('style');
  style.textContent =
    '@keyframes liveUpdateSpin{to{transform:rotate(360deg)}}' +
    `#${OVERLAY_ID} .lu-spinner{width:44px;height:44px;border-radius:50%;` +
    'border:4px solid #e5e7eb;border-top-color:#6eb041;animation:liveUpdateSpin .8s linear infinite}';
  overlay.appendChild(style);

  const spinner = document.createElement('div');
  spinner.className = 'lu-spinner';
  overlay.appendChild(spinner);

  const title = document.createElement('div');
  title.textContent = 'Live update in progress';
  title.style.cssText = 'font-size:18px;font-weight:600;color:#1f2937';
  overlay.appendChild(title);

  const sub = document.createElement('div');
  sub.textContent = subtitle || 'Getting the latest version… this will only take a moment.';
  sub.style.cssText = 'font-size:14px;color:#6b7280;max-width:320px';
  overlay.appendChild(sub);

  document.body.appendChild(overlay);
}

/** Remove the overlay (used when a reload was suppressed and we must fall back). */
export function hideLiveUpdateOverlay(): void {
  document.getElementById(OVERLAY_ID)?.remove();
}
